// Orders-specific table state mappings
import { getSingleValue, getArrayValue } from '@/lib/utils/table-state-utils';

// Filter mappings for orders
export const ordersFilterMapping = {
    // Note: Search is handled separately via the search parameter (full-text search)
    // It searches across order ID, customer email, first name, and last name

    // Store filter
    store: (value: any) => ({
        'stores.id': getArrayValue(value)
    }),

    // Status filter
    status: (value: any) => ({
        'orders.status': getArrayValue(value)
    }),

    // Created from / Source filter
    createdFrom: (value: any) => ({
        'orders.createdFrom': getArrayValue(value)
    }),

    // Draft filter
    draft: (value: any) => ({
        'orders.draft': getArrayValue(value)
    }),

    // Card Last4 search
    cardLast4: (value: any) => ({
        'payment.paymentInstrument.card.last4': { condition: 'contains' as const, value: getSingleValue(value) }
    }),

    // Order Total filter with conditions - values are in minor units (cents)
    orderTotal: (value: any) => {
        if (value && typeof value === 'object' && 'condition' in value) {
            // Handle condition filter { condition: 'is-greater-than', value: [2714] }
            return {
                'summaries.totalAdjustedAmount': value
            };
        }
        return undefined;
    },

    // Date range filter - pass through directly like payments
    createdAt: (value: any) => ({
        date: value
    }),

    // Exclude draft orders
    excludeDrafts: (value: any) => {
        if (value) {
            return { 'orders.excludeDrafts': true };
        }
        return undefined;
    },

    // Unsynced paid orders (paid + no externalOrderId + exclude drafts)
    unsyncedPaid: (value: any) => {
        if (value) {
            return {
                'orders.status': ['paid'],
                'orders.externalOrderId.isNull': true,
                'orders.excludeDrafts': true,
            };
        }
        return undefined;
    },
};

export const ordersQueryParamsMapping = {
    store: (value: URLSearchParams) => {
        return value.get('store')?.split(',')
    },
    status: (value: URLSearchParams) => {
        return value.get('status')?.split(',')
    },
    createdFrom: (value: URLSearchParams) => {
        return value.get('createdFrom')?.split(',')
    },
    draft: (value: URLSearchParams) => {
        return value.get('draft')?.split(',')
    },
    cardLast4: (value: URLSearchParams) => {
        return value.get('cardLast4')
    },
    orderTotal: (value: URLSearchParams) => {
        const res = value.get('orderTotal')
        return res ? JSON.parse(res) : null
    },
    createdAt: (value: URLSearchParams) => {
        const res = value.get('createdAt')
        return res ? JSON.parse(res) : null
    },
}

// Sort field mappings for orders (frontend column ID -> backend sort field)
export const ordersSortMapping = {
    id: 'orders.id' as const,
    createdAt: 'orders.createdAt' as const,
    status: 'orders.status' as const,
    store: 'stores.name' as const,
    orderTotal: 'summaries.totalAdjustedAmount' as const,
    paidAmount: 'paidAmount' as const,
    orderNumber: 'orders.orderNumber' as const,
};
